// src/components/grid/LineManagementModal.tsx
// HANDLES: Full line management - add, remove, edit names, manage semesters
import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, Edit2, Check, X, AlertTriangle, Info, Move, Settings } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { debug } from '../../utils/debug';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
interface Line {
  id: string;
  name?: string;
  semester: 'semester1' | 'semester2' | 'fullyear';
  pairedLineId?: string;
  days?: string[];
}
interface Semester {
  id: string;
  name: string;
  lineIds: string[];
}
interface LineManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  lines: Line[];
  semesters: Semester[];
  onSaveChanges: (lines: Line[], semesters: Semester[]) => Promise<void>;
  classCount: number; // To warn about impact of changes
}
const LineManagementModal: React.FC<LineManagementModalProps> = ({
  isOpen,
  onClose,
  lines: initialLines,
  semesters: initialSemesters,
  onSaveChanges,
  classCount
}) => {
  const [lines, setLines] = useState<Line[]>(initialLines);
  const [semesters, setSemesters] = useState<Semester[]>(initialSemesters);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editingLineName, setEditingLineName] = useState<string>('');
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const confirm = useConfirm();
  const { addToast } = useToast();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      debug('🔧 LineManagement: Modal opened with lines:', initialLines);
      debug('🔧 LineManagement: Total lines count:', initialLines.length);
      debug('🔧 LineManagement: Delete buttons will be', initialLines.length <= 2 ? 'DISABLED' : 'ENABLED');
      debug('🔧 LineManagement: Initial line structure:', initialLines.map(l => ({
        id: l.id,
        name: l.name,
        semester: l.semester,
        hasS1: l.id.includes('S1'),
        hasS2: l.id.includes('S2')
      })));
      debug('🔧 LineManagement: Modal opened with semesters:', initialSemesters);
      setLines(initialLines);
      setSemesters(initialSemesters);
      setHasChanges(false);
      setEditingLineId(null);
      setEditingLineName('');
      setShowAdvanced(false);
    }
  }, [isOpen, initialLines, initialSemesters]);

  // Track changes - ENHANCED
  useEffect(() => {
    const linesChanged = JSON.stringify(lines) !== JSON.stringify(initialLines);
    const semestersChanged = JSON.stringify(semesters) !== JSON.stringify(initialSemesters);
    debug('🔄 LineManagement: Change tracking:', {
      linesChanged,
      semestersChanged,
      currentLines: lines.map(l => ({
        id: l.id,
        name: l.name
      })),
      initialLines: initialLines.map(l => ({
        id: l.id,
        name: l.name
      })),
      hasChanges: linesChanged || semestersChanged
    });
    setHasChanges(linesChanged || semestersChanged);
  }, [lines, semesters, initialLines, initialSemesters]);

  // Generate available line IDs (A-Z) - IMPROVED
  const getNextAvailableLineId = useCallback(() => {
    const usedIds = new Set(lines.map(line => line.id));

    // First try single letters A-Z
    for (let i = 0; i < 26; i++) {
      const id = String.fromCharCode(65 + i); // A-Z
      if (!usedIds.has(id)) {
        return id;
      }
    }

    // If A-Z are all used, try double letters AA, AB, etc.
    for (let i = 0; i < 26; i++) {
      for (let j = 0; j < 26; j++) {
        const id = String.fromCharCode(65 + i) + String.fromCharCode(65 + j);
        if (!usedIds.has(id)) {
          return id;
        }
      }
    }

    // Fallback to timestamp-based ID
    return `LINE_${Date.now()}`;
  }, [lines]);

  // Add new line - FIXED TO PRESERVE EXISTING ID STRUCTURE
  const handleAddLine = (semester: 'semester1' | 'semester2') => {
    // Generate a new line ID that follows the existing pattern
    const existingIds = lines.map(l => l.id);
    let newLineId: string;

    // If we have existing lines with S1/S2 pattern, continue that pattern
    if (existingIds.some(id => id.includes('S1') || id.includes('S2'))) {
      // Find the next available letter for the S1/S2 pattern
      const semesterSuffix = semester === 'semester1' ? 'S1' : 'S2';
      const usedLetters = existingIds.filter(id => id.includes(semesterSuffix)).map(id => id.replace(semesterSuffix, ''));

      // Find next available letter
      for (let i = 0; i < 26; i++) {
        const letter = String.fromCharCode(65 + i); // A, B, C...
        if (!usedLetters.includes(letter)) {
          newLineId = `${letter}${semesterSuffix}`;
          break;
        }
      }
      if (!newLineId!) {
        newLineId = `${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${semesterSuffix}`;
      }
    } else {
      // Use simple A-Z pattern
      newLineId = getNextAvailableLineId();
    }
    const semesterName = semester === 'semester1' ? 'Semester 1' : 'Semester 2';
    const newLine: Line = {
      id: newLineId,
      name: `Line ${newLineId.replace(/S[12]/, '')} - ${semesterName}`,
      // Remove S1/S2 from display name
      semester,
      days: semester === 'semester1' ? ['Monday', 'Wednesday', 'Friday'] : ['Tuesday', 'Thursday']
    };
    debug('🟢 LineManagement: Creating new line:', {
      newLineId,
      existingPattern: existingIds.some(id => id.includes('S1') || id.includes('S2')) ? 'S1/S2 pattern' : 'Simple pattern',
      existingIds,
      newLine
    });

    // Insert line in the right position based on semester
    setLines(prev => {
      const existingLines = [...prev];
      if (semester === 'semester1') {
        // Find the last semester1 line position and insert after it
        let insertIndex = 0;
        for (let i = existingLines.length - 1; i >= 0; i--) {
          const line = existingLines[i];
          if (line.semester === 'semester1' || ['A', 'B', 'C', 'D', 'E', 'F'].includes(line.id) || line.id.includes('S1') || line.id.endsWith('1')) {
            insertIndex = i + 1;
            break;
          }
        }
        existingLines.splice(insertIndex, 0, newLine);
      } else {
        // For semester2, just add to the end
        existingLines.push(newLine);
      }
      return existingLines;
    });

    // Update semester lineIds
    setSemesters(prev => prev.map(sem => {
      if (sem.id === semester) {
        return {
          ...sem,
          lineIds: [...sem.lineIds, newLineId]
        };
      } else if (sem.id === 'fullyear') {
        return {
          ...sem,
          lineIds: [...sem.lineIds, newLineId]
        };
      }
      return sem;
    }));
    debug('🟢 LineManagement: Added new line:', newLine);
  };

  // Remove line
  const handleRemoveLine = async (lineId: string) => {
    debug('🗑️ LineManagement: Attempting to remove line:', lineId);
    debug('🗑️ LineManagement: Current lines before removal:', lines.map(l => l.id));
    debug('🗑️ LineManagement: Total lines count:', lines.length);

    if (lines.length <= 2) {
      debug('🗑️ LineManagement: Cannot remove - only', lines.length, 'lines remaining');
      addToast('Cannot remove line - at least 2 lines are required for the grid to function.', 'warning');
      return;
    }
    if (classCount > 0) {
      const confirmed = await confirm({
        title: `Remove Line ${lineId}?`,
        message: `Warning: Removing Line ${lineId} will affect ${classCount} classes in your grid. ` + `Any teacher assignments to this line will be lost. Are you sure you want to continue?`,
        confirmLabel: 'Remove',
        variant: 'danger',
      });
      if (!confirmed) {
        debug('🗑️ LineManagement: User cancelled line removal');
        return;
      }
    }
    
    debug('🗑️ LineManagement: Proceeding with line removal');
    setLines(prev => {
      const newLines = prev.filter(line => line.id !== lineId);
      debug('🗑️ LineManagement: Lines after removal:', newLines.map(l => l.id));
      return newLines;
    });

    // Update semester lineIds
    setSemesters(prev => prev.map(sem => ({
      ...sem,
      lineIds: sem.lineIds.filter(id => id !== lineId)
    })));

    // Clear any paired line references
    setLines(prev => prev.map(line => {
      if (line.pairedLineId === lineId) {
        return {
          ...line,
          pairedLineId: undefined
        };
      }
      return line;
    }));
    debug('🔴 LineManagement: Removed line:', lineId);
  };

  // Start editing line name
  const handleStartEdit = (lineId: string, currentName: string) => {
    setEditingLineId(lineId);
    setEditingLineName(currentName);
  };

  // Save line name edit
  const handleSaveEdit = () => {
    if (!editingLineId) return;
    setLines(prev => prev.map(line => {
      if (line.id === editingLineId) {
        const updatedLine = {
          ...line,
          name: editingLineName.trim() || `Line ${line.id}`
        };
        debug('✏️ LineManagement: Updated line name:', editingLineId, 'from', line.name, 'to', updatedLine.name);
        return updatedLine;
      }
      return line;
    }));
    setEditingLineId(null);
    setEditingLineName('');
  };

  // Cancel line name edit
  const handleCancelEdit = () => {
    setEditingLineId(null);
    setEditingLineName('');
  };

  // Move line between semesters
  const handleMoveLine = (lineId: string, newSemester: 'semester1' | 'semester2') => {
    const line = lines.find(l => l.id === lineId);
    if (!line || line.semester === newSemester) return;

    // Update line semester
    setLines(prev => prev.map(l => {
      if (l.id === lineId) {
        return {
          ...l,
          semester: newSemester,
          // Clear paired line when moving semesters
          pairedLineId: undefined
        };
      }
      return l;
    }));

    // Update semester lineIds
    setSemesters(prev => prev.map(sem => {
      if (sem.id === line.semester) {
        // Remove from old semester
        return {
          ...sem,
          lineIds: sem.lineIds.filter(id => id !== lineId)
        };
      } else if (sem.id === newSemester) {
        // Add to new semester
        return {
          ...sem,
          lineIds: [...sem.lineIds, lineId]
        };
      }
      return sem;
    }));
    debug('📱 LineManagement: Moved line', lineId, 'from', line.semester, 'to', newSemester);
  };

  // Set line pairing
  const handleSetPairing = (lineId: string, pairedLineId: string | '') => {
    setLines(prev => prev.map(line => {
      if (line.id === lineId) {
        return {
          ...line,
          pairedLineId: pairedLineId || undefined
        };
      }
      // Also update the reverse pairing
      if (line.id === pairedLineId) {
        return {
          ...line,
          pairedLineId: lineId
        };
      }
      // Clear old reverse pairings
      if (line.pairedLineId === lineId && pairedLineId !== line.id) {
        return {
          ...line,
          pairedLineId: undefined
        };
      }
      return line;
    }));
  };

  // Save all changes
  const handleSave = async () => {
    try {
      debug('💾 LineManagement: Starting save process');
      debug('💾 LineManagement: Lines to save:', lines.length, 'lines');
      debug('💾 LineManagement: Line details:', lines.map(l => ({
        id: l.id,
        name: l.name,
        semester: l.semester
      })));
      debug('💾 LineManagement: Semesters to save:', semesters);
      
      await onSaveChanges(lines, semesters);
      debug('💾 LineManagement: Changes saved successfully to backend');
      onClose();
    } catch (err) {
      console.error('💾 LineManagement: Failed to save line changes:', err);
      console.error('💾 LineManagement: Error details:', err);
      addToast('Failed to save changes. Please try again.', 'error');
    }
  };
  const semester1Lines = lines.filter(line => {
    // Check multiple ways to identify semester 1 lines
    return line.semester === 'semester1' || ['A', 'B', 'C', 'D', 'E', 'F'].includes(line.id) || line.id.includes('S1') ||
    // For lines like AS1, BS1, CS1
    line.id.endsWith('1') // For lines ending with 1
    ;
  }).sort((a, b) => {
    // Sort by line ID to maintain order
    return a.id.localeCompare(b.id);
  });
  const semester2Lines = lines.filter(line => {
    // Check multiple ways to identify semester 2 lines
    return line.semester === 'semester2' || ['G', 'H', 'I', 'J', 'K', 'L'].includes(line.id) || line.id.includes('S2') ||
    // For lines like AS2, BS2, CS2
    line.id.endsWith('2') && !line.id.includes('S1') // For lines ending with 2 but not S1
    ;
  }).sort((a, b) => {
    // Sort by line ID to maintain order
    return a.id.localeCompare(b.id);
  });

  // DEBUG: Log line filtering with enhanced detection
  useEffect(() => {
    debug('🔧 LineManagement: Line filtering results:', {
      totalLines: lines.length,
      allLines: lines.map(l => ({
        id: l.id,
        name: l.name,
        semester: l.semester,
        includesS1: l.id.includes('S1'),
        includesS2: l.id.includes('S2'),
        endsWithOne: l.id.endsWith('1'),
        endsWithTwo: l.id.endsWith('2')
      })),
      semester1Count: semester1Lines.length,
      semester2Count: semester2Lines.length,
      semester1Lines: semester1Lines.map(l => ({
        id: l.id,
        name: l.name,
        semester: l.semester
      })),
      semester2Lines: semester2Lines.map(l => ({
        id: l.id,
        name: l.name,
        semester: l.semester
      }))
    });
  }, [lines, semester1Lines, semester2Lines]);
  return <Modal isOpen={isOpen} onClose={onClose} title="Manage Grid Lines" size="large" footer={<div className="flex justify-between items-center w-full">
          <div className="text-sm text-ink-500">
            {lines.length} lines total • {hasChanges ? 'Unsaved changes' : 'No changes'}
          </div>
          <div className="flex space-x-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!hasChanges}>
              Apply Changes
            </Button>
          </div>
        </div>}>
      <div className="space-y-6">
        {/* Impact Warning */}
        {classCount > 0 && hasChanges && <div className="bg-warn-bg border border-warn-border rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-warn-fg mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-warn-fg font-medium">
                  Changing lines will affect {classCount} classes
                </p>
                <p className="text-warn-fg mt-1">
                  Adding/removing lines or changing names will update your grid. 
                  Removing lines will delete all teacher assignments for those lines.
                </p>
              </div>
            </div>
          </div>}

        {/* Line Management Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Semester 1 Lines */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-navy-700">
                Semester 1 Lines ({semester1Lines.length})
              </h3>
              <Button variant="secondary" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => handleAddLine('semester1')}>
                Add Line
              </Button>
            </div>

            <div className="space-y-2">
              {semester1Lines.map(line => <div key={line.id} className="border rounded-lg p-3 bg-navy-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {editingLineId === line.id ? <div className="flex items-center space-x-2">
                          <input type="text" value={editingLineName} onChange={e => setEditingLineName(e.target.value)} className="flex-1 text-sm border border-ink-300 rounded px-2 py-1" autoFocus onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') handleCancelEdit();
                    }} />
                          <button onClick={handleSaveEdit} className="p-1 text-ok-fg hover:bg-ok-bg rounded">
                            <Check className="h-4 w-4" />
                          </button>
                          <button onClick={handleCancelEdit} className="p-1 text-danger-fg hover:bg-danger-bg rounded">
                            <X className="h-4 w-4" />
                          </button>
                        </div> : <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-mono tabular-nums text-lg font-bold text-navy-800">
                              {line.id}
                            </span>
                            <span className="text-sm text-ink-700">
                              {line.name || `Line ${line.id}`}
                            </span>
                          </div>
                          <div className="text-xs text-ink-500 mt-1">
                            ID: {line.id} | Display: {line.name || 'Default'}
                          </div>
                          {line.pairedLineId && <div className="text-xs text-navy-600 mt-1">
                              ↔ Paired with Line {line.pairedLineId}
                            </div>}
                        </div>}
                    </div>

                    <div className="flex items-center space-x-1">
                      <button onClick={() => handleStartEdit(line.id, line.name || `Line ${line.id}`)} className="p-1 text-ink-500 hover:text-navy-600 hover:bg-navy-100 rounded" title="Edit line name">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleMoveLine(line.id, 'semester2')} className="p-1 text-ink-500 hover:text-ok-fg hover:bg-ok-bg rounded" title="Move to Semester 2">
                        <Move className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={(e) => {
                          if (lines.length <= 2) {
                            debug('🚫 LineManagement: Delete button clicked but DISABLED - only', lines.length, 'lines');
                            e.preventDefault();
                            return;
                          }
                          handleRemoveLine(line.id);
                        }} 
                        className={`p-1 rounded ${lines.length <= 2 ? 'text-ink-300 cursor-not-allowed' : 'text-ink-500 hover:text-danger-fg hover:bg-danger-bg'}`} 
                        title={lines.length <= 2 ? `Cannot delete - minimum 2 lines required (currently ${lines.length})` : "Remove line"} 
                        disabled={lines.length <= 2}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Semester 2 Lines */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-ok-fg">
                Semester 2 Lines ({semester2Lines.length})
              </h3>
              <Button variant="secondary" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => handleAddLine('semester2')}>
                Add Line
              </Button>
            </div>

            <div className="space-y-2">
              {semester2Lines.map(line => <div key={line.id} className="border rounded-lg p-3 bg-ok-bg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {editingLineId === line.id ? <div className="flex items-center space-x-2">
                          <input type="text" value={editingLineName} onChange={e => setEditingLineName(e.target.value)} className="flex-1 text-sm border border-ink-300 rounded px-2 py-1" autoFocus onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') handleCancelEdit();
                    }} />
                          <button onClick={handleSaveEdit} className="p-1 text-ok-fg hover:bg-ok-bg rounded">
                            <Check className="h-4 w-4" />
                          </button>
                          <button onClick={handleCancelEdit} className="p-1 text-danger-fg hover:bg-danger-bg rounded">
                            <X className="h-4 w-4" />
                          </button>
                        </div> : <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-mono tabular-nums text-lg font-bold text-ok-fg">
                              {line.id}
                            </span>
                            <span className="text-sm text-ink-700">
                              {line.name || `Line ${line.id}`}
                            </span>
                          </div>
                          <div className="text-xs text-ink-500 mt-1">
                            ID: {line.id} | Display: {line.name || 'Default'}
                          </div>
                          {line.pairedLineId && <div className="text-xs text-navy-600 mt-1">
                              ↔ Paired with Line {line.pairedLineId}
                            </div>}
                        </div>}
                    </div>

                    <div className="flex items-center space-x-1">
                      <button onClick={() => handleStartEdit(line.id, line.name || `Line ${line.id}`)} className="p-1 text-ink-500 hover:text-ok-fg hover:bg-ok-bg rounded" title="Edit line name">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleMoveLine(line.id, 'semester1')} className="p-1 text-ink-500 hover:text-navy-600 hover:bg-navy-100 rounded" title="Move to Semester 1">
                        <Move className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={(e) => {
                          if (lines.length <= 2) {
                            debug('🚫 LineManagement: Delete button clicked but DISABLED - only', lines.length, 'lines');
                            e.preventDefault();
                            return;
                          }
                          handleRemoveLine(line.id);
                        }} 
                        className={`p-1 rounded ${lines.length <= 2 ? 'text-ink-300 cursor-not-allowed' : 'text-ink-500 hover:text-danger-fg hover:bg-danger-bg'}`} 
                        title={lines.length <= 2 ? `Cannot delete - minimum 2 lines required (currently ${lines.length})` : "Remove line"} 
                        disabled={lines.length <= 2}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="border-t pt-4">
          <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center space-x-2 text-sm text-ink-500 hover:text-ink-900">
            <Settings className="h-4 w-4" />
            <span>Advanced Settings</span>
            <span className="text-xs">({showAdvanced ? 'hide' : 'show'})</span>
          </button>

          {showAdvanced && <div className="mt-4 space-y-4 bg-ink-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-ink-700">Line Pairing</h4>
              <p className="text-xs text-ink-500">
                Pair lines between semesters so that assigning a teacher to one automatically assigns them to the paired line.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {semester1Lines.map(line => <div key={line.id} className="flex items-center space-x-2">
                    <span className="font-mono tabular-nums text-sm font-bold w-8">{line.id}</span>
                    <span className="text-sm text-ink-500">↔</span>
                    <select value={line.pairedLineId || ''} onChange={e => handleSetPairing(line.id, e.target.value)} className="flex-1 text-xs border border-ink-300 rounded px-2 py-1">
                      <option value="">No pairing</option>
                      {semester2Lines.map(s2Line => <option key={s2Line.id} value={s2Line.id}>
                          Line {s2Line.id}
                        </option>)}
                    </select>
                  </div>)}
              </div>
            </div>}
        </div>

        {/* Summary */}
        <div className="bg-ink-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Info className="h-4 w-4 text-ink-500" />
            <span className="text-sm font-medium text-ink-700">Summary</span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-ink-500">Total Lines:</span>
              <span className="font-mono tabular-nums ml-2 font-medium">{lines.length}</span>
            </div>
            <div>
              <span className="text-ink-500">Semester 1:</span>
              <span className="font-mono tabular-nums ml-2 font-medium text-navy-600">{semester1Lines.length}</span>
            </div>
            <div>
              <span className="text-ink-500">Semester 2:</span>
              <span className="font-mono tabular-nums ml-2 font-medium text-ok-fg">{semester2Lines.length}</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>;
};
export default LineManagementModal;